/**
 * The page the browser lands on after a Google or GitHub sign-in.
 *
 * GoTrue sends the browser here with a `code` (and the `state` this site produced
 * before navigating away). This page trades the code for a session, stores it, and
 * sends the reader to their account. It exists as a separate route from
 * `/auth/callback`, which is reserved for handing a sign-in back to one of the
 * desktop apps.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Loader2 } from 'lucide-react';

import { completeOAuthSignIn, messageOf } from '../lib/api';
import { useAuth } from '../auth/AuthProvider';
import { useI18n } from '../i18n';
import { Link, useRouter } from '../router';
export const OAuthCallback: React.FC = () => {
  const { establishSession, clearError } = useAuth();
  const { t } = useI18n();
  const { navigate } = useRouter();
  const [busy, setBusy] = useState(false);
  // Held as thrown, resolved at render, so the message survives a language switch.
  const [failure, setFailure] = useState<unknown>(null);

  const params = useMemo(() => new URLSearchParams(window.location.search), []);

  useEffect(() => {
    let cancelled = false;
    const code = params.get('code');
    const state = params.get('state');
    const errorParam = params.get('error');
    const errorDescription = params.get('error_description');

    // The provider refused, or Google/GitHub did. Nothing to exchange.
    if (errorParam !== null && errorParam !== '') {
      if (!cancelled) {
        setFailure(
          errorDescription !== null && errorDescription !== '' ? errorDescription : errorParam,
        );
      }
      return;
    }

    // No code means there is nothing to trade. Send the reader to sign in.
    if (code === null || code === '' || state === null || state === '') {
      if (!cancelled) setFailure('oauth_incomplete');
      return;
    }

    clearError();
    setBusy(true);

    void (async () => {
      try {
        const session = await completeOAuthSignIn(code, state);
        if (cancelled) return;
        await establishSession(session);
        if (cancelled) return;
        navigate('conta');
      } catch (caught) {
        if (!cancelled) setFailure(caught);
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [params, establishSession, navigate, clearError]);

  if (busy) {
    return (
      <section className="mx-auto flex min-h-[70vh] w-full flex-col items-center justify-center px-5 text-center">
        <img src="/OrobasWorkLogoNewGold.svg" alt={t.callback.crestAlt} width={40} height={40} className="block mb-6" />
        <Loader2 className="size-6 animate-spin text-gold" aria-hidden="true" />
        <h1 className="mt-5 text-lg font-semibold text-cream">{t.account.signingIn}</h1>
      </section>
    );
  }

  if (failure !== null) {
    return (
      <section className="mx-auto flex min-h-[70vh] w-full max-w-md flex-col items-center justify-center px-5 text-center">
        <img src="/OrobasWorkLogoNewGold.svg" alt={t.callback.crestAlt} width={40} height={40} className="block mb-6" />
        <h1 className="text-lg font-semibold text-cream">{t.account.oauthFailedTitle}</h1>
        <p className="mt-2 text-sm leading-relaxed text-muted">
          {messageOf(failure, t.account.oauthFailedBody)}
        </p>
        <Link
          to="conta"
          className="pill pressable mt-6 inline-flex items-center gap-2 bg-gold px-6 py-3 text-ink hover:bg-gold-bright"
        >
          {t.account.backToSignIn}
          <ArrowRight className="size-4" aria-hidden="true" />
        </Link>
      </section>
    );
  }

  // Nothing to do and nothing went wrong: a bare visit, sent home to sign in.
  return (
    <section className="mx-auto flex min-h-[70vh] w-full max-w-md flex-col items-center justify-center px-5 text-center">
      <img src="/OrobasWorkLogoNewGold.svg" alt={t.callback.crestAlt} width={40} height={40} className="block mb-6" />
      <h1 className="text-lg font-semibold text-cream">{t.account.oauthFailedTitle}</h1>
      <p className="mt-2 text-sm leading-relaxed text-muted">{t.account.oauthFailedBody}</p>
      <Link
        to="conta"
        className="pill pressable mt-6 inline-flex items-center gap-2 bg-gold px-6 py-3 text-ink hover:bg-gold-bright"
      >
        {t.account.signInAction}
        <ArrowRight className="size-4" aria-hidden="true" />
      </Link>
    </section>
  );
};